import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { X, Download, Smartphone, Share, Plus } from "lucide-react";

export function InstallPrompt() {
  const location = useLocation();
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Don't show on admin or driver pages (they have their own install prompts)
  const isAdminOrDriver = location.pathname.startsWith('/admin') || location.pathname.startsWith('/driver');


  useEffect(() => {
    // Check if already installed or dismissed
    const wasInstalled = window.matchMedia('(display-mode: standalone)').matches;
    const wasDismissed = localStorage.getItem('installPromptDismissed');
    const dismissedTime = localStorage.getItem('installPromptDismissedTime');
    
    // Show again after 7 days
    if (dismissedTime) {
      const daysSinceDismiss = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismiss > 7) {
        localStorage.removeItem('installPromptDismissed');
        localStorage.removeItem('installPromptDismissedTime');
      }
    }

    setIsStandalone(wasInstalled);
    setDismissed(!!wasDismissed);

    // Detect iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Listen for install prompt (Android/Desktop Chrome)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (!wasInstalled && !wasDismissed) {
        setShowPrompt(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Show prompt on mobile devices OR after first visit (for awareness)
    // This helps users know the app can be installed
    const hasSeenPrompt = localStorage.getItem('installPromptSeen');
    if (!wasInstalled && !wasDismissed && !hasSeenPrompt) {
      setTimeout(() => {
        setShowPrompt(true);
        localStorage.setItem('installPromptSeen', 'true');
      }, 3000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  // Auto-dismiss after 5 seconds
  useEffect(() => {
    if (showPrompt) {
      const autoDismissTimer = setTimeout(() => {
        setShowPrompt(false);
      }, 5000);
      return () => clearTimeout(autoDismissTimer);
    }
  }, [showPrompt]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    } else if (!isIOS) {
      alert("To install: tap the browser menu (⋮) and select 'Install app' or 'Add to Home Screen'");
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem('installPromptDismissed', 'true');
    localStorage.setItem('installPromptDismissedTime', Date.now().toString());
  };

  if (isStandalone || dismissed || !showPrompt || isAdminOrDriver) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-in slide-in-from-bottom duration-300" data-testid="install-prompt">
      <div className="max-w-md mx-auto bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-green-600 to-green-500 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-white" />
            <span className="text-white font-semibold text-sm">Add to Home Screen</span>
          </div>
          <button 
            onClick={handleDismiss}
            className="text-white/80 hover:text-white p-1 rounded-full hover:bg-white/20 transition-colors"
            data-testid="dismiss-install-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-14 h-14 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_20e37a3a-e6c9-4384-b519-a00f19f82c1f/artifacts/f12i0vth_Screenshot_20260211_021213_Chrome.jpg" 
                alt="Easy Transport"
                className="w-12 h-12 rounded-lg object-cover"
              />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Easy Transportation</h3>
              <p className="text-sm text-gray-600 mt-0.5">
                Install our app for quick access to bookings, driver tracking & more!
              </p>
            </div>
          </div>

          {isIOS ? (
            // iOS Instructions
            <div className="bg-gray-50 rounded-xl p-3 mb-3">
              <p className="text-xs font-medium text-gray-700 mb-2">How to install:</p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Share className="w-3.5 h-3.5 text-blue-600" />
                  </div>
                  <span>Tap the <strong>Share</strong> button in Safari</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Plus className="w-3.5 h-3.5 text-green-600" />
                  </div>
                  <span>Select <strong>"Add to Home Screen"</strong></span>
                </div>
              </div>
            </div>
          ) : (
            // Android/Desktop Install Button
            <Button 
              onClick={handleInstall}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2"
              data-testid="install-app-btn"
            >
              <Download className="w-5 h-5" />
              Install App
            </Button>
          )}

          <p className="text-xs text-center text-gray-400 mt-3">
            Fast, free & works offline
          </p>
        </div>
      </div>
    </div>
  );
}
